import React, { useState, useEffect, useCallback } from 'react';
// import MUI components
import { Grid2 as Grid, Alert, Snackbar } from '@mui/material';
// import custom components
import { BoxCenterStart } from '../style/box';
import { TypographyBold } from '../style/typography';
import { commonStyles } from "../style/common";
import { fetchData, postData } from "../func/api";
import { validationSchemaRegistPartnerCompany, validationSchemaEditPartnerCompany } from '../func/validation_check';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CommonCard from '../components/common_card';
import { BaseDialog } from '../components/base_dialog';
import PartnerCompanyRegistration from "../components/partner_company_registration";
import PartnerCompanyList from "../components/partner_company_list";
import { DragDropContext } from '@hello-pangea/dnd';
import SkeletonLoading from "../components/skeleton_loading";
import { REPORT_STATUS } from '../const/config';

const MstPartnerCompanyList = () => {
    const [facilities, setFacilities] = useState([]);
    const [partnerCompanies, setPartnerCompanies] = useState([]);
    const [expandedPartnerCompany, setExpandedPartnerCompany] = useState(false);
    const [editingPartnerCompany, setEditingPartnerCompany] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [partnerCompanyToDelete, setPartnerCompanyToDelete] = useState(null);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState("success");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState(null);
    const [dialogData, setDialogData] = useState(null);
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);

    const { control: registControl, handleSubmit: handleRegistSubmit, reset: registReset } = useForm({
        resolver: yupResolver(validationSchemaRegistPartnerCompany()),
        defaultValues: {
            registPartnerCompany: {
                name: "",
                facilities: []
            }
        },
        mode: "onBlur"
    });

    const { control: editControl, handleSubmit: handleEditSubmit, reset: editReset } = useForm({
        resolver: yupResolver(validationSchemaEditPartnerCompany()),
        defaultValues: {
            editingPartnerCompany: {
                uuid: "",
                name: "",
                sort_num: "",
                facilities: []
            }
        },
        mode: "onBlur"
    });

    // データ取得
    useEffect(() => {
        const fetchDataAll = async () => {
            try {
                const [powerplantList, partnerCompanyList] = await Promise.all([
                    fetchData(`api/get_powerplant_list`),
                    fetchData(`api/get_partner_company_list`),
                ]);

                setFacilities(powerplantList);           // 発電所リスト
                setPartnerCompanies(partnerCompanyList); // 連絡先会社一覧

                // 地震一覧データ取得
                const earthquakeData = await fetchData(`api/get_earthquake_list/`);
                // 業務対中の場合は、編集不可
                const reportingData = earthquakeData.find(obj =>
                    obj.report_status_uuid === REPORT_STATUS.UNSUPPORTED ||
                    obj.report_status_uuid === REPORT_STATUS.FISRTREPORT ||
                    obj.report_status_uuid === REPORT_STATUS.PRIMARYCHECK ||
                    obj.report_powerplant_status_uuid === REPORT_STATUS.POWERPLANTSUPPORT
                );
                setIsDisabled(reportingData ? true : false);
            } catch (error) {
                console.error("データ取得中にエラーが発生しました:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchDataAll();
    }, [refreshFlag]);

    // 編集対象の連絡先会社が変わったらフォームをリセット
    useEffect(() => {
        if (editingPartnerCompany) {
            editReset({
                editingPartnerCompany: {
                    uuid: editingPartnerCompany.uuid,
                    name: editingPartnerCompany.name || "",
                    sort_num: editingPartnerCompany.sort_num || "",
                    facilities: editingPartnerCompany.facilities || []
                }
            });
        }
    }, [editingPartnerCompany, editReset]);

    const openDialog = (type, data = null) => {
        setDialogType(type);
        setDialogData(data);
        setDialogOpen(true);
    };

    // ダイアログ「キャンセル」押下時
    const handleCancelEdit = () => {
        setExpandedPartnerCompany(false);
        setEditingPartnerCompany(null);
    };

    const handleDialogConfirm = () => {
        switch (dialogType) {
            case 'register':
                handleRegistSubmit(handleConfirm)(); // 登録処理
                break;
            case 'delete':
                handleDeleteConfirm(); // 削除処理
                break;
            case 'save':
                handleSaveConfirm(); // 保存処理
                break;
            default:
                break;
        }
        setDialogOpen(false);
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
    };

    const handleConfirm = async () => {
        try {
            // 現在の partnerCompanies の最大 sort_num を取得
            const existingSortNums = partnerCompanies.map(pc => Number(pc.sort_num) || 0);
            const maxSortNum = existingSortNums.length > 0 ? Math.max(...existingSortNums) : 0;
            const nextSortNum = maxSortNum + 1;

            // 登録用データ作成
            const data = {
                name: dialogData.name,
                sort_num: nextSortNum,
                facilities: dialogData.facilities
            };
            // 登録API呼び出し
            await postData("api/post_regist_partner_company", data);

            setAlertMessage("登録が完了しました");
            setAlertSeverity("success");
            setAlertOpen(true);
            setRefreshFlag(prev => !prev);
            registReset();
        } catch (err) {
            console.error(err);
            setAlertMessage("登録に失敗しました");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    };

    const handleEditClick = (partnerCompany) => {
        setEditingPartnerCompany({
            ...partnerCompany,
            facilities: partnerCompany.facilities.map(f => f.uuid),
            sort_num: partnerCompany.sort_num
        });
        setExpandedPartnerCompany(partnerCompany.uuid);
    };

    const openDeleteDialog = (partnerCompany) => {
        setDialogType("delete");
        setDialogData(partnerCompany); // 削除対象
        setDialogOpen(true);
    };

    // 「削除」ボタン押下時
    const handleDeleteClick = (partnerCompany) => {
        if (partnerCompanies.length === 1) {
            setAlertMessage("連絡先会社は最低1つ必要です。");
            setAlertSeverity("error");
            setAlertOpen(true);
            return;
        }
        openDeleteDialog(partnerCompany);
        setPartnerCompanyToDelete(partnerCompany);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            // 削除対象を除外した全体リスト
            const updatedList = partnerCompanies
                .filter(pc => pc.uuid !== partnerCompanyToDelete.uuid)
                .sort((a, b) => a.sort_num - b.sort_num)
                .map((pc, index) => ({
                    ...pc,
                    sort_num: index + 1
                }));

            // DB更新：全件の sort_num を更新
            for (const pc of updatedList) {
                await postData("api/post_update_partner_company_sort_num", {
                    partner_company_uuid: pc.uuid,
                    sort_num: pc.sort_num
                });
            }

            // 論理削除 API 呼び出し
            await postData("api/post_delete_partner_company", { partner_company_uuid: partnerCompanyToDelete.uuid });

            // state 更新
            setPartnerCompanies(updatedList);

            setAlertMessage("連絡先会社の削除と並び順の更新が完了しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
        } catch (err) {
            console.error("削除失敗しました：", err);
            setAlertMessage("連絡先会社の削除に失敗しました。再度時間をおいてからお試しください。");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setDeleteDialogOpen(false);
            setPartnerCompanyToDelete(null);
            setExpandedPartnerCompany(false);
            setEditingPartnerCompany(null);
            setRefreshFlag(prev => !prev);
        }
    };

    // 「保存」ボタンのダイアログ「OK」押下時
    const handleSaveConfirm = async () => {
        const formData = dialogData;
        try {
            const data = {
                partner_company_uuid: formData.uuid,
                name: formData.name,
                sort_num: formData.sort_num,
                facilities: formData.facilities
            };

            await postData("api/post_update_partner_company", data);
            setPartnerCompanies(
                partnerCompanies.map((pc) =>
                    pc.uuid === formData.uuid ? formData : pc
                )
            );

            setAlertMessage("連絡先会社の更新が完了しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
            setRefreshFlag(prev => !prev);
        } catch (err) {
            console.error("更新失敗しました：", err);
            setAlertMessage("連絡先会社の更新に失敗しました。");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setExpandedPartnerCompany(false);
            setEditingPartnerCompany(null);
            setDialogOpen(false);
        }
    };

    const onDragEnd = useCallback(async (result) => {
        const { source, destination } = result;
        if (!destination) return; // ドロップ先なしなら何もしない

        // 並び替え関数
        const reorder = (list, startIndex, endIndex) => {
            const result = Array.from(list);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result;
        };

        // 並び替え後に sort_num を振り直す
        const reordered = reorder(partnerCompanies, source.index, destination.index)
            .map((pc, index) => ({ ...pc, sort_num: index + 1 }));

        // state 更新
        setPartnerCompanies(reordered);

        // DB 更新
        try {
            for (const pc of reordered) {
                await postData("api/post_update_partner_company_sort_num", {
                    partner_company_uuid: pc.uuid,
                    sort_num: pc.sort_num
                });
            }
            setAlertMessage("連絡先会社の並び順を更新しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
        } catch (err) {
            console.error("並び順更新に失敗しました：", err);
            setAlertMessage("並び順の更新に失敗しました。再度お試しください。");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    }, [partnerCompanies]);

    if (loading) {
        return (<SkeletonLoading />);
    }

    return (
        <BoxCenterStart sx={commonStyles.page}>
            <Grid
                container
                spacing={7}
                columns={12}
                sx={commonStyles.container}
            >
                {/* Page Title */}
                <Grid size={{ xs: 12 }}>
                    <CommonCard size={{ xs: 12 }}
                        child={
                            <TypographyBold
                                sx={{ textAlign: 'center', padding: '10px 0' }}
                            >
                                連絡先会社管理
                            </TypographyBold>
                        }
                    />
                </Grid>
                {/* 新規連絡先会社登録 */}
                <PartnerCompanyRegistration
                    control={registControl}
                    facilities={facilities}
                    openDialog={openDialog}
                    handleRegistSubmit={handleRegistSubmit}
                    expanded={true}
                    isDisabled={isDisabled}
                />
                <DragDropContext onDragEnd={onDragEnd}>
                    {/* 連絡先会社一覧 */}
                    <PartnerCompanyList
                        control={editControl}
                        partnerCompanies={partnerCompanies}
                        expandedPartnerCompany={expandedPartnerCompany}
                        facilities={facilities}
                        handleEditClick={handleEditClick}
                        handleCancelEdit={handleCancelEdit}
                        handleDeleteClick={handleDeleteClick}
                        handleEditSubmit={handleEditSubmit}
                        openDialog={openDialog}
                        title="連絡先会社一覧"
                        droppableId="partner-company"
                        isDisabled={isDisabled}
                    />
                </DragDropContext>
                {/* 確認ダイアログ */}
                <BaseDialog
                    open={dialogOpen}
                    onClose={handleDialogCancel}
                    title={
                        dialogType === "register" ? "連絡先会社登録" :
                            dialogType === "delete" ? "連絡先会社削除確認" :
                                dialogType === "save" ? "連絡先会社保存確認" : ""
                    }
                    message={
                        dialogType === "register" ? "連絡先会社を登録します。よろしいですか？" :
                            dialogType === "delete" ? `連絡先会社「${dialogData?.name}」を削除しますか？\nこの操作は取り消せません。` :
                                dialogType === "save" ? "編集内容を保存します。よろしいですか？" : ""
                    }
                    onConfirm={handleDialogConfirm}
                    confirmText={
                        dialogType === "register" ? "OK" :
                            dialogType === "delete" ? "削除" :
                                dialogType === "save" ? "保存" : "OK"
                    }
                    cancelText="キャンセル"
                />
                {/* 失敗時のSnackbar */}
                {alertMessage && (
                    <Snackbar
                        open={alertOpen}
                        autoHideDuration={3000}
                        onClose={() => setAlertOpen(false)}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    >
                        <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
                            {alertMessage}
                        </Alert>
                    </Snackbar>
                )}
            </Grid>
        </BoxCenterStart>
    );
};

export default MstPartnerCompanyList;